/**
 * Reports list — `/client-reports/reports`. Scoped to the GLOBAL active brand
 * (switched from the bottom-left sidebar button), like every sibling tab.
 *
 * Two buckets:
 *   - "New reports to check" — the drafts still awaiting a human (prominent).
 *   - "Past reports"        — everything already decided (collapsed <details>).
 *
 * Both come from ONE unfiltered request partitioned client-side: the backend's
 * `?status=` is a single exact value and the review bucket spans two statuses,
 * so filtering server-side would cost three round-trips with three independent
 * limits. In the demo it can also preview the white-label client portal.
 */
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import SubPageInnerContentHolder from '../../components/subPageContentHolder/SubPageInnerContentHolder';
import LoadingSpinner from '../../ui/loadingSpinner/LoadingSpinner';
import { HOME_ROUTE } from '../../lib/constants/route';
import { setMessage } from '../../store/reducers/appSlice';
import type { RootState } from '../../store/store';
import {
  listClientReports,
  type ClientReportSummary,
} from '../../lib/api/client-reports/client-reports-api';
import { isDemoMode } from '../../lib/demo/demo-mode';
import { isNewDemoMode } from '../../lib/demo-new/demo-mode';
import ClientReportsNav from './ClientReportsNav';
import {
  partitionReports,
  periodLabel,
  statusTagLabel,
  formatDate,
} from './reports-list-helpers';
import './client-reports.scss';

// Demo-only, and lazily loaded so the standalone demo portal never lands in the
// production bundle. It is a self-contained HTML preview with no coupling back
// into this page or the report pipeline. Each demo brings its own copy.
const WhitelabelPortalPreview = lazy(
  () => import('../demo/components/WhitelabelPortalPreview')
);
const WhitelabelPortalPreviewNew = lazy(
  () => import('../demo-new/components/WhitelabelPortalPreview')
);

// The backend clamps to 200. At one report a month that is ~16 years for a
// single brand, so the review bucket can never be truncated in practice.
const PAGE_LIMIT = 200;

interface CardProps {
  report: ClientReportSummary;
  onOpen: (id: string) => void;
  prominent?: boolean;
}

/**
 * A report row. Deliberately a <button>, not a div: the visual "block" comes
 * from CSS, while the button gives keyboard access and a role for free.
 */
const ReportCard = ({ report, onOpen, prominent }: CardProps) => {
  const { t, i18n } = useTranslation();
  const period = periodLabel(report, i18n.language, t);
  return (
    <button
      type='button'
      onClick={() => onOpen(report.id)}
      className={`client-reports__card${
        prominent ? ' report-list__check-card' : ''
      }`}
    >
      <span className='client-reports__card-main'>
        {prominent ? (
          <span className='report-list__period'>{period}</span>
        ) : null}
        <span className='client-reports__card-title'>
          {prominent ? report.title : period}
        </span>
        <span className='client-reports__card-meta'>
          {prominent ? null : <span>{report.title}</span>}
          {report.created_by_type === 'ai' ? (
            <span>{t('ReportsListAiGenerated', 'AI-generated')}</span>
          ) : null}
        </span>
      </span>
      <span className='client-reports__card-side'>
        <span
          className={`client-reports__tag client-reports__tag--${report.status}`}
        >
          {statusTagLabel(report.status, t)}
        </span>
        <span>
          {formatDate(report.published_at || report.created_at, i18n.language)}
        </span>
      </span>
    </button>
  );
};

const ReportsListPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  // Read inside the fetch through a ref, so a language switch never refetches.
  const tRef = useRef(t);
  tRef.current = t;

  const activeShopSlug = useSelector(
    (s: RootState) => s.user?.profile?.active_shop_slug || ''
  );
  const allShops = useSelector(
    (s: RootState) => s.user?.profile?.all_shops || []
  );
  const activeShopName = useMemo(
    () =>
      allShops.find((sh: any) => sh?.slug === activeShopSlug)?.name ||
      activeShopSlug,
    [allShops, activeShopSlug]
  );

  const [reports, setReports] = useState<ClientReportSummary[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [portalOpen, setPortalOpen] = useState(false);

  const loadReports = useCallback(
    async (slug: string) => {
      setLoading(true);
      try {
        const rows = await listClientReports({
          shop_slug: slug,
          limit: PAGE_LIMIT,
        });
        setReports(rows);
      } catch (err: any) {
        // Never swallow this: rendering "no reports yet" on a 500 is a lie.
        const msg =
          err?.status === 403
            ? tRef.current(
                'AgentConfigNoBrandAccess',
                'You do not have access to that brand.'
              )
            : err?.message ||
              tRef.current('ReportsListLoadFailed', 'Failed to load reports.');
        dispatch(setMessage({ message: msg, messageType: 'error' }));
        setReports([]);
      } finally {
        setLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (activeShopSlug) loadReports(activeShopSlug);
  }, [activeShopSlug, loadReports]);

  const { toCheck, past } = useMemo(
    () => partitionReports(reports || []),
    [reports]
  );

  const openReport = useCallback(
    (id: string) => navigate(`/client-reports/${id}`),
    [navigate]
  );

  const hasAny = toCheck.length + past.length > 0;

  return (
    <SubPageInnerContentHolder
      title={t('ClientReports', 'Reporting')}
      backBtnLink={HOME_ROUTE}
    >
      <ClientReportsNav />

      <div className='report-list'>
        <div className='report-list__brand-field'>
          <label className='report-list__label'>
            {t('ClientReportsActiveBrand', 'Active brand')}
          </label>
          <div className='client-reports__active-brand'>
            <span className='client-reports__active-brand-name'>
              {activeShopName || '—'}
            </span>
            <span className='client-reports__active-brand-hint'>
              {t(
                'ClientReportsSwitchBrandHint',
                'Switch brand from the button in the bottom-left.'
              )}
            </span>
          </div>
        </div>

        {!activeShopSlug ? (
          <div className='client-reports__empty'>
            {t(
              'AgentsNoActiveBrand',
              'No active brand. Pick one from the button in the bottom-left.'
            )}
          </div>
        ) : loading || reports === null ? (
          <div style={{ marginTop: '3rem', textAlign: 'center' }}>
            <LoadingSpinner
              customLoadingMsg={t('ReportsListLoading', 'Loading reports…')}
            />
          </div>
        ) : (
          <>
            <section className='report-list__section'>
              <h3 className='report-list__heading'>
                {t('ReportsListToCheckHeading', 'New reports to check')}
              </h3>
              <p className='report-list__hint'>
                {t(
                  'ReportsListToCheckHint',
                  'Drafts TARA filed for {{brand}}. Open one to review and edit — approve it from the action queue to publish it to the client portal.',
                  { brand: activeShopName }
                )}
              </p>
              {toCheck.length === 0 ? (
                <div className='client-reports__empty'>
                  {t(
                    'ReportsListToCheckEmpty',
                    'Nothing waiting. TARA drafts a report at the end of each period and files it here.'
                  )}
                </div>
              ) : (
                <div className='client-reports__list'>
                  {toCheck.map((r) => (
                    <ReportCard
                      key={r.id}
                      report={r}
                      onOpen={openReport}
                      prominent
                    />
                  ))}
                </div>
              )}
            </section>

            <details className='report-list__past'>
              <summary className='report-list__past-summary'>
                {past.length > 0
                  ? t('ReportsListPastCount', 'Past reports ({{total}})', {
                      total: past.length,
                    })
                  : t('ReportsListPast', 'Past reports')}
              </summary>
              <div className='report-list__past-body'>
                {past.length === 0 ? (
                  <div className='client-reports__empty'>
                    {t(
                      'ReportsListPastEmpty',
                      'No past reports for this brand yet.'
                    )}
                  </div>
                ) : (
                  <div className='client-reports__list'>
                    {past.map((r) => (
                      <ReportCard key={r.id} report={r} onOpen={openReport} />
                    ))}
                  </div>
                )}
              </div>
            </details>
          </>
        )}

        {(isDemoMode() || isNewDemoMode()) && hasAny && (
          <div className='report-list__demo'>
            <div>
              <div className='report-list__demo-title'>
                {t('ReportsListDemoTitle', 'See what your clients see 👀')}
              </div>
              <div className='report-list__demo-copy'>
                {t(
                  'ReportsListDemoCopy',
                  'Every report ships in a white-label, branded client portal on your own domain — each client signs in to their own.'
                )}
              </div>
            </div>
            <button
              type='button'
              className='report-list__demo-btn'
              onClick={() => setPortalOpen(true)}
            >
              {t('ReportsListDemoButton', 'Preview client portal ↗')}
            </button>
          </div>
        )}
      </div>

      {portalOpen && (
        <Suspense fallback={null}>
          {isNewDemoMode() ? (
            <WhitelabelPortalPreviewNew onClose={() => setPortalOpen(false)} />
          ) : (
            <WhitelabelPortalPreview onClose={() => setPortalOpen(false)} />
          )}
        </Suspense>
      )}
    </SubPageInnerContentHolder>
  );
};

export default ReportsListPage;
